import type { MailMessageSummary } from "@/lib/types/mail";

const holdsByFolder = new Map<string, Set<number>>();

type TombstoneEntry = { exp: number; unseen: boolean };

const tombstonesByFolder = new Map<string, Map<number, TombstoneEntry>>();

const TOMBSTONE_TTL_MS = 15 * 60_000;
/** Anciennes lignes provisoires, au-dessus de tout UID IMAP. Elles ne sont plus créées. */
const OPTIMISTIC_UID_BASE = 10_000_000_000;

function folderHoldKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function uidKey(uid: unknown): number {
  const n = typeof uid === "number" ? uid : Number(uid);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/** Ligne inventée pour un déplacement : elle ne doit jamais rester dans une liste. */
export function isOptimisticGhostUid(uid: unknown): boolean {
  return uidKey(uid) >= OPTIMISTIC_UID_BASE;
}

function pruneExpiredTombstones(key: string, now = Date.now()): void {
  const map = tombstonesByFolder.get(key);
  if (!map) return;
  for (const [uid, entry] of map) {
    if (entry.exp <= now) map.delete(uid);
  }
  if (map.size === 0) tombstonesByFolder.delete(key);
}

export function tombstoneUids(
  accountId: number,
  folder: string,
  uids: number[],
  ttlMs = TOMBSTONE_TTL_MS,
  unseenUids?: ReadonlySet<number>
): void {
  if (uids.length === 0) return;
  const key = folderHoldKey(accountId, folder);
  pruneExpiredTombstones(key);
  let map = tombstonesByFolder.get(key);
  if (!map) {
    map = new Map();
    tombstonesByFolder.set(key, map);
  }
  const expiresAt = Date.now() + ttlMs;
  for (const uid of uids) {
    const id = uidKey(uid);
    if (id <= 0) continue;
    const prev = map.get(id);
    const unseen = unseenUids?.has(id) === true || prev?.unseen === true;
    map.set(id, { exp: expiresAt, unseen });
  }
}

/** Non-lus masqués par une suppression récente — à retirer d'un compteur IMAP en retard. */
export function countUnseenTombstones(accountId: number, folder: string): number {
  const key = folderHoldKey(accountId, folder);
  pruneExpiredTombstones(key);
  const map = tombstonesByFolder.get(key);
  if (!map || map.size === 0) return 0;
  let count = 0;
  for (const entry of map.values()) {
    if (entry.unseen) count += 1;
  }
  return count;
}

/**
 * Pastille : le STATUS IMAP peut encore compter les non-lus qu'on vient de retirer.
 * On soustrait ce masque, sans descendre sous le compteur déjà ajusté à l'écran.
 */
export function folderUnreadAfterDeleteHold(
  accountId: number,
  folder: string,
  imapUnread: number,
  localUnread: number | undefined
): number {
  const hidden = countUnseenTombstones(accountId, folder);
  if (hidden <= 0) return imapUnread;
  const adjusted = Math.max(0, imapUnread - hidden);
  if (localUnread == null) return adjusted;
  return Math.max(localUnread, adjusted);
}

export function isUidTombstoned(accountId: number, folder: string, uid: number): boolean {
  const id = uidKey(uid);
  if (id <= 0) return false;
  const key = folderHoldKey(accountId, folder);
  pruneExpiredTombstones(key);
  return tombstonesByFolder.get(key)?.has(id) === true;
}

export function filterTombstonedMessages(
  accountId: number,
  folder: string,
  messages: MailMessageSummary[]
): MailMessageSummary[] {
  const key = folderHoldKey(accountId, folder);
  pruneExpiredTombstones(key);
  const map = tombstonesByFolder.get(key);
  if (!map || map.size === 0) return messages;
  return messages.filter((m) => !map.has(uidKey(m.uid)));
}

export function holdMutationUids(accountId: number, folder: string, uids: number[]): void {
  if (uids.length === 0) return;
  tombstoneUids(accountId, folder, uids);
  const key = folderHoldKey(accountId, folder);
  let set = holdsByFolder.get(key);
  if (!set) {
    set = new Set();
    holdsByFolder.set(key, set);
  }
  for (const uid of uids) {
    const id = uidKey(uid);
    if (id > 0) set.add(id);
  }
}

function removeTombstoneUids(accountId: number, folder: string, uids: number[]): void {
  if (uids.length === 0) return;
  const key = folderHoldKey(accountId, folder);
  pruneExpiredTombstones(key);
  const map = tombstonesByFolder.get(key);
  if (!map) return;
  for (const uid of uids) {
    map.delete(uidKey(uid));
  }
  if (map.size === 0) {
    tombstonesByFolder.delete(key);
  }
}

/** Retire le tombstone (ex. réveil snooze Gmail local — le mail est toujours dans INBOX). */
export function clearMutationTombstoneUids(
  accountId: number,
  folder: string,
  uids: number[]
): void {
  removeTombstoneUids(accountId, folder, uids);
}

export function releaseMutationUids(accountId: number, folder: string, uids: number[]): void {
  const key = folderHoldKey(accountId, folder);
  const set = holdsByFolder.get(key);
  if (set) {
    for (const uid of uids) {
      set.delete(uidKey(uid));
    }
    if (set.size === 0) {
      holdsByFolder.delete(key);
    }
  }
  removeTombstoneUids(accountId, folder, uids);
}

/** Succès move/snooze : libère le hold mais garde le tombstone (évite fantôme IMAP en chaîne). */
export function releaseMutationHoldsOnly(accountId: number, folder: string, uids: number[]): void {
  if (uids.length === 0) return;
  const key = folderHoldKey(accountId, folder);
  const set = holdsByFolder.get(key);
  if (!set) return;
  for (const uid of uids) {
    set.delete(uidKey(uid));
  }
  if (set.size === 0) {
    holdsByFolder.delete(key);
  }
}

export function clearMutationHolds(accountId: number, folder: string): void {
  const key = folderHoldKey(accountId, folder);
  holdsByFolder.delete(key);
  tombstonesByFolder.delete(key);
}

/** Libère les tombstones sans toucher aux holds actifs (ex. bouton Actualiser). */
export function clearFolderMutationTombstones(accountId: number, folder: string): void {
  const key = folderHoldKey(accountId, folder);
  tombstonesByFolder.delete(key);
}

export function hasMutationHold(accountId: number, folder: string): boolean {
  const set = holdsByFolder.get(folderHoldKey(accountId, folder));
  return set != null && set.size > 0;
}

export function hasAnyMutationHold(): boolean {
  for (const set of holdsByFolder.values()) {
    if (set.size > 0) return true;
  }
  return false;
}

export function filterHeldMutationMessages(
  accountId: number,
  folder: string,
  messages: MailMessageSummary[]
): MailMessageSummary[] {
  const held = holdsByFolder.get(folderHoldKey(accountId, folder));
  const withoutHeld =
    held && held.size > 0 ? messages.filter((m) => !held.has(uidKey(m.uid))) : messages;
  return filterTombstonedMessages(accountId, folder, withoutHeld);
}

/** Retire holds, tombstones et d’anciennes lignes provisoires. */
export function applyOptimisticMessages(
  accountId: number,
  folder: string,
  messages: MailMessageSummary[]
): MailMessageSummary[] {
  return filterHeldMutationMessages(accountId, folder, messages).filter(
    (message) => !isOptimisticGhostUid(message.uid)
  );
}
